import { useEffect, useState } from 'react';
import axios from 'axios';
import { PaperComponent } from 'components/GovernmentRfi/GovRfiHistoryModal';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useAppSelector } from 'hooks/redux';
import { selectRfi } from 'store/rfiSlice';
import { RfiStatus } from 'types/RfiTypes';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, timelineItemClasses, TimelineSeparator } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography } from '@mui/material';

interface IVendorRfiHistoryModalProps {
    show:boolean
    handleClose:()=>void
}

const VendorRfiHistoryModal = (props:IVendorRfiHistoryModalProps) => {

    //hooks
    const rfi = useAppSelector(selectRfi);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(localizedFormat);
    dayjs.extend(advancedFormat);

    //States
    const [historyData, setHistoryData] = useState<any | null | undefined>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any | null>(null);

    //Get history data
    useEffect(() => {
        if (rfi.rfi?.rfiId) {
            getHistoryData();
        }
    }, [rfi.rfi?.rfiId]);

    const getHistoryData = async () => {
        try {
            setIsLoading(true);
            const result = await axios.get(`/api/v1/Rfi/${rfi.rfi?.rfiId}/history`);
            if (result.data) {
                setHistoryData(result.data);
            }
        }
        catch (error:  any) {
            console.log(error);
        }
        finally {
            setIsLoading(false);
        }
    };

    const downloadRfiPdf = async (record: any) => {
        try {
        setSelectedRecord(record);
        setIsDownloading(true);
        await fetch(`/api/v1/rfi/print/${record.rfiId}`).then((response) => {
            response.blob().then((blob) => {
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = url;
            a.download = `${record.rfiNumber}_${record.versionNumber}.pdf`;
            a.rel = "noopener";
            a.click();
            });
        });
        } catch (error) {
        console.log(error);
        } finally {
        setSelectedRecord(null);
        setIsDownloading(false);
        }
    };

    return (
        <Dialog
            open={props.show}
            fullWidth
            maxWidth="sm"
            aria-label="RFI History"
            aria-labelledby="history-dialog-title"
            tabIndex={-1}
            PaperComponent={PaperComponent}
        >
            <DialogTitle style={{ cursor: "move" }} id="history-dialog-title">
                <h1 className="h5">
                    <i className="fa-solid fa-clock-rotate-left"></i> {`${rfi.rfi?.rfiNumber}`} History
                </h1>         
                <Typography variant={"subtitle1"} sx={{ fontStyle: "italic" }}>
                    To view a prior version of this RFI, click the link for that
                    version.
                </Typography>
            </DialogTitle>
        <DialogContent>
          <div className="d-flex justify-content-around">
            <div className="col-12 p-2">
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                }}
              >
                {rfi.rfi?.versions?.map((record: any, i:number) => {
                  return (
                    <TimelineItem key={i}>
                      <TimelineSeparator>
                        <TimelineDot></TimelineDot>
                        <TimelineConnector></TimelineConnector>
                      </TimelineSeparator>
                      <TimelineContent>
                        <div className="d-flex flex-nowrap">
                          {record.status === RfiStatus.POSTED && (
                            <Stack direction="column">
                              <Link
                                href="#"
                                onClick={() => downloadRfiPdf(record)}
                              >
                                <span style={{ fontWeight: 600 }}>
                                  {record.versionNumber === 0
                                    ? "RFI Posted to SAM"
                                    : "Update Posted to SAM"}
                                </span>
                                {record.versionNumber != 0 && (
                                  <span style={{ fontWeight: 100 }}>
                                    &nbsp;( Amendment: 000{record.versionNumber}{" "}
                                    )
                                  </span>
                                )}
                              </Link>
                              {isDownloading && record === selectedRecord && (
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontStyle: "italic", fontWeight: 50 }}
                                >
                                  Downloading RFI...
                                </Typography>
                              )}
                            </Stack>
                          )}
                        </div>
                        <div className="d-flex flex-row">
                          {record.actionDate
                            ? dayjs.utc(record.actionDate).tz("America/New_York").format("lll z")
                            : "(Date)"}
                        </div>
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </Timeline>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="btn btn-primary k-mr-2"
            onClick={props.handleClose}
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    )
}

export default VendorRfiHistoryModal